// src/features/userprofile/AdminDirectoryPanel.tsx
// Platform-wide profile directory for admins/moderators — search or browse
// every profile, inspect the privacy-bypassing admin record, and act on it
// (ban/unban, ban history, delete-all-profiles-for-user). See docs/ux/userprofile-ia.md.
//
// SearchUserprofiles' generated store method drops its own query params (a
// codegen gap, same class documented in resource-ia.md) — fixed at the call
// site by calling the raw endpoint function directly.

import { useState } from 'react';
import { observer } from '@react';
import { useUserprofile, useI18n } from '@react/hooks';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import { SearchUserprofiles } from '@core/api/endpoints/userprofile/search_userprofiles';
import type { UserprofileSearchResult, Userprofile, ProfileBanRecord } from '@core/types/userprofile';

const DELETE_KEYWORD = 'DELETE';

function formatTimestamp(ts: string | undefined): string {
  if (!ts) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ts));
  } catch {
    return ts;
  }
}

export const AdminDirectoryPanel = observer(() => {
  const profileStore = useUserprofile();
  const { t } = useI18n();

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserprofileSearchResult[] | null>(null);
  const [browseResults, setBrowseResults] = useState<Userprofile[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selected, setSelected] = useState<Userprofile | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [banHistory, setBanHistory] = useState<ProfileBanRecord[] | null>(null);
  const [showBanHistory, setShowBanHistory] = useState(false);

  // Ban form
  const [banOpen, setBanOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banUntil, setBanUntil] = useState('');
  const [banComment, setBanComment] = useState('');
  const [banBusy, setBanBusy] = useState(false);

  // Danger zone — delete all profiles for this user
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const handleSearch = async () => {
    setListLoading(true);
    setError(null);
    try {
      if (query.trim()) {
        const resp = await SearchUserprofiles({ q: query.trim(), limit: 50 });
        setSearchResults(resp.results?.results ?? []);
      } else {
        setSearchResults(null);
        await profileStore.ListUserprofilesAdmin({ pagination: { limit: 50, cursor: '', direction: '' } } as never);
        setBrowseResults([...profileStore.items]);
      }
    } catch (err) {
      setError(err);
    } finally {
      setListLoading(false);
    }
  };

  const handleBrowseAll = async () => {
    setQuery('');
    setSearchResults(null);
    setListLoading(true);
    setError(null);
    try {
      await profileStore.ListUserprofilesAdmin({ pagination: { limit: 50, cursor: '', direction: '' } } as never);
      setBrowseResults([...profileStore.items]);
    } catch (err) {
      setError(err);
    } finally {
      setListLoading(false);
    }
  };

  const openDetail = async (profileId: string) => {
    setDetailLoading(true);
    setError(null);
    setBanHistory(null);
    setShowBanHistory(false);
    setBanOpen(false);
    setDeleteConfirmOpen(false);
    try {
      const profile = await profileStore.GetUserprofileAdmin(profileId);
      setSelected(profile);
    } catch (err) {
      setError(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleLoadBanHistory = async () => {
    if (!selected?.entity?.id) return;
    setShowBanHistory(true);
    if (banHistory) return;
    try {
      const resp = await profileStore.ListUserprofileBanHistory(selected.entity.id);
      setBanHistory(resp.ban_records?.records ?? []);
    } catch (err) {
      setError(err);
    }
  };

  const handleBan = async () => {
    if (!selected?.entity?.id || !banReason.trim()) return;
    setBanBusy(true);
    setError(null);
    try {
      await profileStore.BanUserprofile(selected.entity.id, {
        profile: {} as never,
        reason: banReason.trim(),
        ban_until: banUntil ? new Date(banUntil).toISOString() : undefined,
        admin_comment: banComment.trim(),
      });
      setSuccess(t('userprofile.admin.banApplied'));
      setBanOpen(false);
      setBanReason('');
      setBanUntil('');
      setBanComment('');
      setBanHistory(null);
      await openDetail(selected.entity.id);
    } catch (err) {
      setError(err);
    } finally {
      setBanBusy(false);
    }
  };

  const handleUnban = async () => {
    if (!selected?.entity?.id) return;
    setBanBusy(true);
    setError(null);
    try {
      await profileStore.UnbanUserprofile(selected.entity.id, { admin_comment: '' });
      setSuccess(t('userprofile.admin.banLifted'));
      setBanHistory(null);
      await openDetail(selected.entity.id);
    } catch (err) {
      setError(err);
    } finally {
      setBanBusy(false);
    }
  };

  const handleDeleteAllProfiles = async () => {
    const userId = selected?.user?.id;
    if (!userId) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await profileStore.DeleteUserprofileByUserID(userId);
      setSuccess(t('userprofile.admin.allProfilesDeleted'));
      setSelected(null);
      setDeleteConfirmOpen(false);
      setDeleteConfirmText('');
      handleBrowseAll();
    } catch (err) {
      setError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const rows: Array<{ id: string; username: string; displayName: string; status?: string }> = searchResults
    ? searchResults.map((r) => ({ id: r.entity?.id ?? '', username: r.username, displayName: r.display_name, status: r.status }))
    : browseResults.map((p) => ({ id: p.entity?.id ?? '', username: p.username, displayName: p.display_name, status: p.status }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.admin.directoryTitle')}</CardTitle>
            <CardDescription>{t('userprofile.admin.directoryDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!!error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
            {success && <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>{success}</Alert>}
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('userprofile.admin.searchPlaceholder')}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="flex-1"
              />
              <Button onClick={handleSearch} isLoading={listLoading}>{t('common.search')}</Button>
            </div>
            {query && (
              <Button variant="ghost" size="sm" onClick={handleBrowseAll}>{t('userprofile.admin.clearSearch')}</Button>
            )}

            <div className="max-h-[28rem] overflow-y-auto divide-y divide-border">
              {rows.length === 0 && !listLoading && (
                <p className="text-sm text-muted-foreground py-6 text-center">{t('userprofile.admin.noProfilesFound')}</p>
              )}
              {rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => openDetail(row.id)}
                  className={`flex w-full items-center justify-between gap-2 py-2.5 text-left transition-colors ${
                    selected?.entity?.id === row.id ? 'text-primary' : 'hover:text-foreground text-foreground/90'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.displayName || row.username || row.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground truncate">@{row.username || '-'}</p>
                  </div>
                  {row.status && <Badge variant="secondary" size="sm">{row.status.replace('PROFILE_STATUS_', '')}</Badge>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.admin.profileDetail')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t('authn.admin.selectProvider') /* reused generic copy */}</p>
            ) : detailLoading ? (
              <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-6 rounded bg-muted animate-pulse" />)}</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-lg font-semibold text-foreground">{selected.display_name || selected.username}</p>
                    <p className="text-sm text-muted-foreground">@{selected.username}</p>
                  </div>
                  <Badge variant={selected.ban ? 'error' : selected.status === 'PROFILE_STATUS_ACTIVE' ? 'success' : 'secondary'}>
                    {selected.ban ? t('userprofile.admin.banned') : (selected.status ?? '').replace('PROFILE_STATUS_', '')}
                  </Badge>
                </div>

                <dl className="divide-y divide-border">
                  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.id')}</dt>
                    <dd className="mt-0.5 text-sm text-foreground font-mono sm:col-span-2 sm:mt-0">{selected.entity?.id}</dd>
                  </div>
                  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.createdAt')}</dt>
                    <dd className="mt-0.5 text-sm text-foreground sm:col-span-2 sm:mt-0">{formatTimestamp(selected.created_at as unknown as string)}</dd>
                  </div>
                  {selected.ban && (
                    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('userprofile.admin.banReason')}</dt>
                      <dd className="mt-0.5 text-sm text-foreground sm:col-span-2 sm:mt-0">{selected.ban.reason}</dd>
                    </div>
                  )}
                </dl>

                <div className="flex flex-wrap gap-2">
                  {selected.ban ? (
                    <Button variant="secondary" size="sm" onClick={handleUnban} isLoading={banBusy}>{t('userprofile.admin.unban')}</Button>
                  ) : (
                    <Button variant="danger" size="sm" onClick={() => setBanOpen((v) => !v)}>{t('userprofile.admin.ban')}</Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={handleLoadBanHistory}>{t('userprofile.admin.banHistory')}</Button>
                </div>

                {banOpen && (
                  <div className="rounded-lg border border-destructive/40 p-3 space-y-2">
                    <Input label={t('userprofile.admin.banReasonLabel')} value={banReason} onChange={(e) => setBanReason(e.target.value)} />
                    <Input label={t('userprofile.admin.banUntilLabel')} type="date" value={banUntil} onChange={(e) => setBanUntil(e.target.value)} />
                    <Input label={t('userprofile.admin.adminComment')} value={banComment} onChange={(e) => setBanComment(e.target.value)} />
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setBanOpen(false)}>{t('common.cancel')}</Button>
                      <Button variant="danger" size="sm" onClick={handleBan} isLoading={banBusy} disabled={!banReason.trim()}>
                        {t('userprofile.admin.ban')}
                      </Button>
                    </div>
                  </div>
                )}

                {showBanHistory && (
                  <Table hoverable>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('userprofile.admin.banReasonLabel')}</TableHead>
                        <TableHead>{t('common.createdAt')}</TableHead>
                        <TableHead>{t('userprofile.admin.expiresAt')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(banHistory ?? []).length === 0 && <TableEmpty colSpan={3} message={t('userprofile.admin.noBanHistory')} />}
                      {(banHistory ?? []).map((rec, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{rec.reason}</TableCell>
                          <TableCell className="text-sm">{formatTimestamp(rec.banned_at as unknown as string)}</TableCell>
                          <TableCell className="text-sm">{formatTimestamp(rec.expires_at as unknown as string) }</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </CardContent>
          {selected && (
            <CardFooter className="flex-col items-stretch gap-2 border-t border-destructive/30">
              <p className="text-xs font-medium text-destructive">{t('userprofile.admin.dangerZone')}</p>
              {!deleteConfirmOpen ? (
                <Button variant="danger" size="sm" onClick={() => setDeleteConfirmOpen(true)} disabled={!selected.user?.id}>
                  {t('userprofile.admin.deleteAllProfiles')}
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{t('userprofile.admin.deleteAllProfilesWarning')}</p>
                  <p className="text-sm text-muted-foreground">{t('security.logoutAllTypeToConfirm', { keyword: DELETE_KEYWORD })}</p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={DELETE_KEYWORD}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
                    autoComplete="off"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }}>{t('common.cancel')}</Button>
                    <Button variant="danger" size="sm" onClick={handleDeleteAllProfiles} isLoading={deleteBusy} disabled={deleteConfirmText !== DELETE_KEYWORD}>
                      {t('userprofile.admin.deleteAllProfiles')}
                    </Button>
                  </div>
                </div>
              )}
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
});

export default AdminDirectoryPanel;
